import type { App } from 'vue';
import type { Router } from 'vue-router';
import VueGtag from 'vue-gtag-next';

/**
 * GA4設定
 * 環境別のMeasurement IDを一元管理
 */
const GA_CONFIG: Record<string, string> = {
  production: 'G-JN0NWS7298',
  staging: 'G-3PQB2LSPDD',
};

/**
 * 現在の環境を取得
 */
export const getEnvironment = (): string => {
  return import.meta.env.VITE_ENV || 'localhost';
};

/**
 * 環境に対応するMeasurement IDを取得
 */
export const getMeasurementId = (): string => {
  const env = getEnvironment();
  return GA_CONFIG[env] || '';
};

/**
 * GA4を初期化しVueアプリに統合する
 * @param app Vueアプリインスタンス
 * @param router Vue Router
 * @param getOrganizationLabel 組織ラベル取得関数
 * @param getTenantId テナントID取得関数
 */
export const setupGoogleAnalytics = (
  app: App,
  router: Router,
  getOrganizationLabel: () => string,
  getTenantId: () => string
): void => {
  const env = getEnvironment();
  const measurementId = getMeasurementId();

  if (!measurementId) {
    // eslint-disable-next-line no-console
    console.log(`[GA4] Skipped: No measurementId for environment "${env}"`);
    return;
  }

  app.use(VueGtag, {
    property: {
      id: measurementId,
      params: {
        send_page_view: false, // 初回の自動送出を明示的にOFF
      },
    },
    useDebugger: env !== 'production',
  });

  router.afterEach((to) => {
    // gtag関数がwindow上に生えているか確認
    const w = window as any;
    const gtagFn = w.gtag || w.dataLayer?.push;

    if (typeof gtagFn === 'function') {
      const organization = getOrganizationLabel();
      const tenantId = getTenantId();

      // route_name や path の揺れに対応するため to.path.includes('/allocation') とする
      const isAllocation = to.path.includes('/allocation');

      let alloc_range: number | undefined;
      if (isAllocation && to.query.start_time && to.query.end_time) {
        const start = new Date(String(to.query.start_time));
        const end = new Date(String(to.query.end_time));
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          alloc_range = Math.round(
            (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
          );
        }
      }

      const eventParams: Record<string, unknown> = {
        page_path: to.path,
        page_location: window.location.origin + to.fullPath,
        page_title: to.meta?.title || document.title,
        ...(organization && { organization }),
        ...(tenantId && { tenant_id: tenantId }),
        ...(isAllocation &&
          to.query.start_time && {
            alloc_start_time: String(to.query.start_time),
          }),
        ...(isAllocation &&
          to.query.end_time && { alloc_end_time: String(to.query.end_time) }),
        ...(alloc_range !== undefined && { alloc_range }),
      };

      w.gtag('event', 'page_view', eventParams);
    }
  });

  // eslint-disable-next-line no-console
  console.log(`[GA4] Initialized: ${measurementId} (${env})`);
};
